import React, { useEffect, useRef } from 'react';

import { useDispatch } from 'react-redux';
import { Box, CircularProgress, Typography } from '@mui/material';

import PKDToggleSwitch from 'components/PKDSwitch';
import { Container } from 'shared-components/Container';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import { useUpsertPersonAttributeMutation } from 'store/api/pkd/endpoints/person';
import { BasicSkeleton } from 'components/SkeletonLoaders';
import { openSnackbar } from 'store/application';
import {
  getAttributesPayload,
  getPersonAttribute,
  getPersonMedicalRecords,
} from 'pages/MedicalInformation/utils';

export const PulseSurvey = ({ isOptingIn, handleIsPulseSurveyOpenModal }) => {
  const dispatch = useDispatch();
  const { person, isPersonLoading, personId, isPersonAttributesFetching } =
    useGetPersonAndAttributes();
  const [upsertPersonAttribute, { isLoading: isPersonAttributeUpserting }] =
    useUpsertPersonAttributeMutation();

  const pulseSurveyTriggered = useRef(false);

  const personInfo = getPersonMedicalRecords(person?.attributes, {
    pulseSurveyOptIn : '',
    phone            : '',
  });
  const enabled = personInfo?.pulseSurveyOptIn === 'yes';

  const handleSavePulseSurveyOptIn = async () => {
    // Call API to opt-in or out
    const attributes = getPersonAttribute(person?.attributes, personInfo);
    const body = getAttributesPayload(
      { pulseSurveyOptIn: enabled ? 'no' : 'yes' },
      attributes
    );
    if (body.length > 0) {
      // upsert attributes
      const { error } = await upsertPersonAttribute({
        body,
        personId,
      });
      if (error) {
        return dispatch(
          openSnackbar({
            type    : 'error',
            message : 'Could not complete request. Please try again',
          })
        );
      }
    }

    dispatch(
      openSnackbar({
        type    : 'success',
        message : 'Pulse survey request successfully saved',
      })
    );
  };

  const handleEnable = async () => {
    pulseSurveyTriggered.current = true;
    if (!personInfo?.phone && !enabled) {
      return handleIsPulseSurveyOpenModal();
    }

    handleSavePulseSurveyOptIn();
  };

  useEffect(() => {
    if (isOptingIn) {
      handleSavePulseSurveyOptIn();
    }
  }, [isOptingIn]);

  return (
    <Container
      maxWidth={{ xl: '800px' }} mb={4}>
      <Typography
        variant='h6' mb={1}>
        Pulse-survey
      </Typography>
      <Typography
        variant='body2' mb={4} color='primary.grey5'>
        Click on the &apos;ON&apos; button to enable the ability to receive the Weekly
        Pulse Surveys via SMS. Note: your mobile number will not be used to send
        SMS messages for any other purpose.
      </Typography>

      {isPersonLoading
        ? (
          <BasicSkeleton
            height='50px' width='100px'
          />
        )
        : (
          <Box
            sx={{ display: 'flex', marginTop: '10px', alignItems: 'center' }}>
            <Box
              sx={{
                margin        : '2px 0px 0px 11px',
                display       : 'flex',
                flexDirection : 'column',
                gap           : '15px',
              }}>
              <Box
                sx={{ display: 'flex' }}>
                <PKDToggleSwitch
                  onChange={handleEnable}
                  disabled={
                    isPersonAttributeUpserting || isPersonAttributesFetching
                  }
                  switcher={enabled ? 'on' : 'off'}
                  data-testid={'pulse-survey-toggle'}
                />
                <Box
                  ml={2}>Receive Pulse-survey via sms</Box>
                {(isPersonAttributeUpserting || isPersonAttributesFetching) &&
                pulseSurveyTriggered.current && (
                  <CircularProgress
                    sx={{ marginLeft: 1 }}
                    color='inherit'
                    size={20}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
    </Container>
  );
};
